import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authErrorMessage, useAuth } from "./AuthProvider";

const FEATURES = [
  "in-built coding IDE",
  "Create an Unlimited Project's",
  "Distribute the project's output",
  "Manage Project",
  "Multiple programming languages support",
] as const;

/**
 * Matches live code.genieacademy.com login:
 * hero + Google OAuth + OR + email/password + Features footer (#0072b4).
 */
export function LoginPage() {
  const { user, loading, signIn, signInWithGoogle } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  async function onEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    /* fixed scroll container so Features is not clipped by body overflow:hidden */
    <div className="home-page fixed inset-0 z-50 flex flex-col overflow-y-auto bg-[#084A7A]">
      <div className="login-page flex flex-1 flex-col items-center px-4 pb-16 pt-10">
          <div className="auth-container relative z-10 flex w-full justify-center">
            <main className="auth-card w-full max-w-[360px] rounded-[15px] bg-[#ffffffd6] p-[15px] shadow-lg">
              {/* Google — staff / Google accounts */}
              <button
                type="button"
                disabled={submitting}
                onClick={() => void onGoogle()}
                className="login-form-button flex w-full items-center justify-center rounded bg-[#473d5d] px-3 py-2.5 text-base font-medium text-white hover:bg-[#554a70] disabled:opacity-60"
              >
                <img
                  className="google-idp-icon mr-3 h-[30px] w-[30px]"
                  alt=""
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                />
                <span className="google-text">Sign in with Google</span>
              </button>

              <div className="login-divider mt-4 flex items-center gap-2.5 text-black">
                <div className="line h-0 w-full border-t-2 border-[#473d5b]" />
                <div className="shrink-0 text-sm font-medium">OR</div>
                <div className="line h-0 w-full border-t-2 border-[#473d5b]" />
              </div>

              <form onSubmit={onEmailSubmit} className="auth-divider mt-4">
                <input
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mb-3 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none [color-scheme:light] focus:border-[#00a4d6]"
                  placeholder="name@icodegenie.com"
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none [color-scheme:light] focus:border-[#00a4d6]"
                  placeholder="Password"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="email-submit mt-2.5 w-full rounded-[10px] border border-[#00a4d6] bg-[#00a4d6] py-2 text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60"
                >
                  {submitting ? "…" : "Login"}
                </button>
              </form>

              {error && (
                <p className="login-form-input-error mt-3 text-center text-sm text-ide-error" role="alert">
                  {error}
                </p>
              )}
            </main>
          </div>

          <img
            src="/logo.png"
            alt="iCode Genie"
            className="mt-10 w-full max-w-[520px] select-none"
          />

          <p className="headline mt-4 w-full px-4 text-center text-base font-bold text-white sm:text-xl">
            Coding Portal for Genie Academy&apos;s coding students.
          </p>
      </div>

      {/* Features block — legacy .footer (#0072b4); needed as scroll target after Google / email auth */}
      <footer id="features" className="footer shrink-0 bg-[#0072b4] px-4 py-8 text-white">
        <h2 className="title mb-6 text-center font-heading text-2xl font-semibold">Features</h2>
        <div className="feature mx-auto grid max-w-5xl grid-cols-1 gap-y-[30px] sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((t) => (
            <div key={t} className="flex items-center gap-2.5">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-white" />
              <h5 className="m-0 text-base font-normal">{t}</h5>
            </div>
          ))}
        </div>
        <div className="container mx-auto mt-8 max-w-5xl">
          <ul className="mb-3 list-none space-y-1 p-0">
            <li>
              <a
                className="text-white underline hover:text-white/90"
                href="https://www.genieacademy.com/about-us/privacy-policy?hsLang=en"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>
            </li>
            <li>
              <a
                className="text-white underline hover:text-white/90"
                href="https://www.genieacademy.com/about-us/terms-of-use?hsLang=en"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Use
              </a>
            </li>
          </ul>
          <p className="m-0 text-sm text-white/95">
            You can code your projects using HTML/CSS/JavaScript/Python. You need an
            activation from a Genie Academy Coach before you can use this.
          </p>
        </div>
      </footer>
    </div>
  );
}
